let registrationPromise: Promise<ServiceWorkerRegistration | null> | null =
  null;
let waitingWorker: ServiceWorker | null = null;

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  // The dev server changes module URLs on every restart; a cached app shell
  // would then serve stale modules and a second React copy. Production only.
  if (import.meta.env.MODE === "development") return Promise.resolve(null);
  if (!("serviceWorker" in navigator)) return Promise.resolve(null);
  registrationPromise ??= navigator.serviceWorker
    .register("/service-worker.js")
    .then((registration) => {
      if (registration.waiting) announceUpdate(registration.waiting);
      registration.addEventListener("updatefound", () => {
        const worker = registration.installing;
        if (!worker) return;
        worker.addEventListener("statechange", () => {
          if (
            worker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            announceUpdate(worker);
          }
        });
      });
      return registration;
    })
    .catch(() => null);
  return registrationPromise;
}

export async function activateWaitingUpdate(): Promise<boolean> {
  const registration = await registerServiceWorker();
  const worker = waitingWorker ?? registration?.waiting ?? null;
  if (!worker) return false;
  return new Promise<boolean>((resolve) => {
    let changed = false;
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => {
        if (changed) return;
        changed = true;
        resolve(true);
        window.location.reload();
      },
      { once: true },
    );
    worker.postMessage({ type: "ACTIVATE_UPDATE" });
    setTimeout(() => resolve(false), 5_000);
  });
}

export async function requestPersistentStorage(): Promise<boolean | null> {
  if (!navigator.storage?.persisted || !navigator.storage.persist) return null;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}

export async function enablePushNotifications(): Promise<void> {
  if (!("Notification" in window) || !("PushManager" in window)) {
    throw new Error("Push notifications are not supported on this device.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }
  const response = await fetch("/api/v1/push-configuration", {
    credentials: "include",
  });
  const config = (await response.json()) as {
    enabled: boolean;
    publicKey: string | null;
  };
  if (!response.ok || !config.enabled || !config.publicKey) {
    throw new Error("Push notifications are not configured on this server.");
  }
  const registration = await registerServiceWorker();
  if (!registration) throw new Error("The service worker is not available.");
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeBase64Url(config.publicKey),
  });
  const payload = subscription.toJSON();
  const saved = await fetch("/api/v1/push-subscriptions", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      endpoint: payload.endpoint,
      p256dh: payload.keys?.p256dh,
      auth: payload.keys?.auth,
      userAgent: navigator.userAgent,
    }),
  });
  if (!saved.ok) {
    await subscription.unsubscribe();
    throw new Error("The push subscription could not save.");
  }
}

export async function disablePushNotifications(): Promise<void> {
  const registration = await registerServiceWorker();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  const response = await fetch("/api/v1/push-subscriptions", {
    method: "DELETE",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
  if (!response.ok)
    throw new Error("The push subscription could not be removed.");
  await subscription.unsubscribe();
}

function announceUpdate(worker: ServiceWorker): void {
  waitingWorker = worker;
  window.dispatchEvent(new CustomEvent("fairway:update-ready"));
}

function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(
    (value + padding).replaceAll("-", "+").replaceAll("_", "/"),
  );
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}
